import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { columnsTotalWidth } from '@swimlane/ngx-datatable/release/utils';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<object>;
  public currentUser: Observable<object>;
  private loginURL: string;
  // public menuRoutes = [];

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router
  ) {
    this.currentUserSubject = new BehaviorSubject<object>(
      JSON.parse(localStorage.getItem('currentUser'))
    );
    this.currentUser = this.currentUserSubject.asObservable();
    const protocol = location.protocol || 'http:';
    if (isDevMode()) {
      this.loginURL = `${protocol}//ts.cteam.com.tw/api/login`;
    } else {
      this.loginURL = `${protocol}//webapi.cteam.com.tw/api/login`;
    }
  }

  public get currentUserValue(): object {
    return this.currentUserSubject.value;
  }

  public get menuSidebar(): any {
    return this.currentUserSubject.value['client_info']['menu']['menuRoutes'];
  }

  // 建立路由
  createRouter() {
    // 如尚未取得登入功能則跳過
    if (!this.currentUserSubject.value) {
      return;
    }
    const appRoutes = [...this.router.config];
    const features =
      this.currentUserSubject.value['client_info']['menu'].features;
    appRoutes[0].children.length = 0;
    appRoutes[0].children.push({
      path: '',
      redirectTo: '/authentication/login',
      pathMatch: 'full'
    });
    // 遍歷功能清單
    for (const feature in features) {
      if (features.hasOwnProperty(feature)) {
        const element = features[feature];
        element.list.forEach((list: any, i: number) => {
          // 參數不完整轉跳
          if (i === 0) {
            appRoutes[0].children.push({
              path: feature,
              redirectTo: `/${feature}/${list.name}${
                feature === 'catalogue' ? '/productInfo' : ''
              }`,
              pathMatch: 'full'
            });
          }
          if (feature === 'catalogue') {
            appRoutes[0].children.push({
              path: `${feature}/${list.name}`,
              redirectTo: `/${feature}/${list.name}/productInfo`,
              pathMatch: 'full'
            });
            // 依照功能清單轉跳被禁止的功能
            for (const expFeatureName in list.features) {
              const expFeature = list.features[expFeatureName];
              // 如果被禁止就轉強制轉跳
              if (!expFeature.enable) {
                appRoutes[0].children.push({
                  path: `${feature}/${list.name}/${expFeatureName}`,
                  redirectTo: `/${feature}/${list.name}/productInfo`,
                  pathMatch: 'full'
                });
              }
            }
            // 如果沒有groupbuy功能就轉跳網址
            if (!list.features.hasOwnProperty('groupbuy')) {
              appRoutes[0].children.push({
                path: `${feature}/${list.name}/groupbuy`,
                redirectTo: `/${feature}/${list.name}/productInfo`,
                pathMatch: 'full'
              });
            }
          }
          // 延遲載入的module
          const loadChildren = {
            announcement:
              './project/web/announcement/announcement.module#AnnouncementModule',
            resource: './project/web/resource/resource.module#ResourceModule',
            catalogue:
              './project/web/catalogue/catalogue.module#CatalogueModule',
            advertisement:
              './project/web/advertisement/advertisement.module#AdvertisementModule',
            activity: './project/web/activity/activity.module#ActivityModule',
            gallery: './project/web/gallery/gallery.module#GalleryModule',
            venue: './project/web/venue/venue.module#VenueModule',
            blog: './project/web/blog/blog.module#BlogModule'
          }[feature];
          if (loadChildren) {
            appRoutes[0].children.push({
              path: `${feature}/${list.name}`,
              loadChildren: loadChildren,
              data: { title: `${list.name}｜形象網站雲端後台` }
            });
          }
        });
      }
    }
    // 重新設定路由
    this.router.resetConfig(appRoutes);
  }

  // 切換專案
  changeProject(project_name: string) {
    // 取得專案資訊
    this.getInformation(
      this.currentUserSubject.value['project_info'][project_name]['project_id']
    ).subscribe((information) => {
      // 儲存功能清單
      this.currentUserSubject.value['client_info']['menu'] = information;
      // 儲存當前專案名稱
      this.currentUserSubject.value['client_info']['now'] = project_name;
      // 建立清單
      this.createMenu(information['features']);
      // 建立路由
      this.createRouter();
      // 儲存專案資訊
      localStorage.setItem(
        'currentUser',
        JSON.stringify(this.currentUserSubject.value)
      );
      this.currentUserSubject.next(this.currentUserSubject.value);
      // 轉跳回首頁
      this.router.navigate([
        this.currentUserSubject.value['client_info']['home']
      ]);
      // 重新載入元件
      // this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      //   this.router.navigate([
      //     this.currentUserSubject.value['client_info']['home']
      //   ]);
      // });
    });
  }

  // 取得專案資訊
  getInformation(project_id: number) {
    return this.http.get(`/project/information?project_id=${project_id}`);
  }

  // 建立功能清單
  createMenu(features: object) {
    const menuRoutes = [];
    // 遍歷第一層功能清單
    for (const feature in features) {
      if (features.hasOwnProperty(feature)) {
        const element = features[feature];
        // 第一層功能
        const itemObject_1 = {
          path: '',
          title: element.info.name,
          icon: {
            announcement: 'mdi mdi-comment-text-outline',
            resource: 'mdi mdi-file-outline',
            catalogue: 'mdi mdi-cart-outline',
            advertisement: 'fab fa-adversal',
            activity: 'mdi mdi-calendar',
            gallery: 'mdi mdi-image-filter',
            venue: 'mdi mdi-map-marker',
            blog: 'mdi mdi-map-marker'
          }[feature],
          class: 'has-arrow',
          extralink: false,
          submenu: []
        };
        // 遍歷第二層功能清單
        element.list.forEach((list: any, i: number) => {
          // 第二層功能
          const itemObject_2 = {
            path: '/' + feature + '/' + list.name,
            title: list.name,
            icon: '',
            class: '',
            extralink: false,
            submenu: []
          };
          // 遍歷第三層功能清單 (固定功能)
          if (feature === 'catalogue') {
            itemObject_2.class = 'has-arrow';
            // 第三層基本功能
            const itemObject_3_1 = {
              path: '/' + feature + '/' + list.name + '/' + 'productInfo',
              title: '產品清單',
              icon: '',
              class: 'p-l-10',
              extralink: false,
              submenu: []
            };
            // 第三層基本功能
            const itemObject_3_2 = {
              path: '/' + feature + '/' + list.name + '/' + 'listInfo',
              title: '訂單資訊',
              icon: '',
              class: 'p-l-10',
              extralink: false,
              submenu: []
            };
            // 第三層擴充功能
            const itemObject_3_3 = {
              path: '/' + feature + '/' + list.name + '/' + 'groupbuy',
              title: '團購接龍',
              icon: '',
              class: 'p-l-10',
              extralink: false,
              submenu: []
            };
            itemObject_2.submenu.push(itemObject_3_1);
            itemObject_2.submenu.push(itemObject_3_2);
            // 遍歷第三層擴充功能資訊
            for (const featureName in list.features) {
              const feature = list.features[featureName];
              // 如果有啟動再加入功能列表
              if (feature.enable) {
                itemObject_2.submenu.push(itemObject_3_3);
              }
            }
          }

          itemObject_1.submenu.push(itemObject_2);
        });
        menuRoutes.push(itemObject_1);
      }
    }

    // 固定資源功能在最後
    const index = menuRoutes.findIndex((item) => item.title === '資源功能');
    if (index > -1) {
      const [removedObject] = menuRoutes.splice(index, 1);
      menuRoutes.push(removedObject);
    }

    this.currentUserSubject.value['client_info']['menu']['menuRoutes'] =
      menuRoutes;
    this.currentUserSubject.value['client_info']['home'] =
      menuRoutes[0]['submenu'][0]['path'];
  }

  // 登入功能
  login(username: string, password: string) {
    return this.http
      .post<any>(this.loginURL, {
        username,
        password
      })
      .pipe(
        map((user: any) => {
          // 判斷回傳狀態
          if (user && user.status) {
            // token 儲存到 cookie 1天後過期
            this.cookieService.set(
              'SESSIONID',
              user['client_info'].token,
              1,
              '/',
              '',
              false,
              'Lax'
            );
            delete user['client_info']['token'];
            // 預設第一個專案
            user['client_info']['now'] = Object.keys(user['project_info'])[0];
            // 儲存專案資訊
            localStorage.setItem('currentUser', JSON.stringify(user));
            this.currentUserSubject.next(user);
          }
          return user;
        })
      );
  }

  // 登出功能
  logout() {
    localStorage.removeItem('currentUser');
    this.cookieService.delete('SESSIONID', '/');
    this.currentUserSubject.next(null);
  }
}
